import { CircleText, QuestionDisplayMode } from '@doltech/ui/lib/figma/CircleText/CircleText';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { Box } from '@doltech/ui/lib/figma/Common/Box';
import DragIn, { OPTION_LIST_WIDTH } from '@doltech/ui/lib/figma/QuestionWithSpots/DragIn';
import { QuestionTypeEnum } from '@doltech/utils/lib/constants';
import { numberToABC } from '@doltech/utils/lib/text';
import { motion } from 'framer-motion';
import { isNil } from 'lodash';
import { observer } from 'mobx-react-lite';
import { getSnapshot } from 'mobx-state-tree';
import * as React from 'react';
import { useDragLayer } from 'react-dnd';
import styled from 'styled-components';

import { DoTestDragInInstance } from '../student-do-test/model/Question/do-test-drag-in.store';
import { dragLayerStyle, getItemStyles } from './DragOutAnswer';

interface DragInProps extends React.HTMLAttributes<HTMLDivElement> {
  data: DoTestDragInInstance;
}

export const DragInAnswer = observer((props: DragInProps) => {
  const dragConstraints = React.useRef(null);
  const { data, ...rest } = props;
  const { no, questions, answer, removeAnswer, userAnswers, image, options } = data;
  const floatingBoxRef = React.useRef(null);
  const [containerHeight, setContainerHeight] = React.useState(undefined);

  React.useEffect(() => {
    setTimeout(() => {
      setContainerHeight(floatingBoxRef.current?.clientHeight);
    }, 100);
  }, [containerHeight, floatingBoxRef]);

  return (
    <motion.div ref={dragConstraints}>
      <Container className="dol-question" height={containerHeight} {...rest}>
        <DragIn
          id={`question-no-${no}`}
          no={no}
          ref={floatingBoxRef}
          image={image}
          options={options}
          spots={questions}
          answered={getSnapshot(userAnswers)}
          onAnswer={answer}
          onRemoveAnswer={removeAnswer}
        />
      </Container>
      <DragPreview />
    </motion.div>
  );
});

const Container = styled<any>(Box)<{ height }>`
  min-height: ${(p) => p.height}px;
  height: max-content;
`;

const DragPreview = () => {
  const { itemType, isDragging, item, initialOffset, currentOffset } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    itemType: monitor.getItemType(),
    initialOffset: monitor.getInitialSourceClientOffset(),
    currentOffset: monitor.getSourceClientOffset(),
    isDragging: monitor.isDragging(),
  }));

  if (itemType === QuestionTypeEnum.DRAG_IN && isDragging && !isNil(item)) {
    return (
      <div style={dragLayerStyle} className={`drag-in-drag-preview-${item.dragData.text}`}>
        <div style={getItemStyles(initialOffset, currentOffset)}>
          <DragInPreview
            displayMode={QuestionDisplayMode.QUESTION_OPTION_DRAG}
            circleText={numberToABC(item.dragData.idx + 1)}
            text={item.dragData.text}
          />
        </div>
      </div>
    );
  }

  return null;
};

const DragInPreview = styled(CircleText)`
  background-color: ${colorsV2.white100};
  border-radius: 16px;
  border: 1px dashed ${colorsV2.gray20};
  padding: 4px;
  width: ${OPTION_LIST_WIDTH}px;
  box-shadow: 0 16px 24px rgba(0, 0, 0, 0.06), 0 2px 6px rgba(0, 0, 0, 0.04),
    0 0 1px rgba(0, 0, 0, 0.04);
  opacity: 0.6;

  .question-circle.question-option-drag {
    background-color: ${colorsV2.blue60};
    border: none;
  }
`;
